import { Column } from '@tanstack/react-table';
import { MultiSelect } from '@carbon/react';
import { useEffect, useMemo, useRef } from 'react';
import { DataTableColumnMeta } from './types';
import { DropdownItem } from './header-select-filter';
// Side-effect import: loads global CSS fix for Carbon checkbox label spacing
import './header-multi-select-filter.module.scss';

// ─────────────────────────────────────
// Props
// ─────────────────────────────────────

interface HeaderMultiSelectFilterProps<TData, TValue> {
  column: Column<TData, TValue>;
}

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Inline multi-select filter for the table header filter row.
 * Displays a compact multi-select dropdown using Carbon MultiSelect.
 * Options are derived from column meta.filterOptions or auto-generated for boolean types.
 * @param {HeaderMultiSelectFilterProps} props - Component props
 * @param {Column} props.column - TanStack Table column instance
 * @return {JSX.Element} The header multi-select filter element
 */
export function HeaderMultiSelectFilter<TData, TValue>({
  column,
}: HeaderMultiSelectFilterProps<TData, TValue>) {
  const meta = column.columnDef.meta as DataTableColumnMeta | undefined;
  const rawFilterValue = column.getFilterValue() as string[] | undefined;
  const filterValue = useMemo(() => rawFilterValue ?? [], [rawFilterValue]);
  const options = useMemo(() => {
    if (meta?.filterOptions) return meta.filterOptions;
    if (meta?.type === 'boolean') {
      return [
        { value: 'true', label: 'true' },
        { value: 'false', label: 'false' },
      ];
    }
    return [];
  }, [meta?.filterOptions, meta?.type]);
  const keyRef = useRef(0);

  // Selected items as DropdownItem objects
  const selectedItems = useMemo(
    () => options.filter((opt) => filterValue.includes(opt.value)),
    [options, filterValue]
  );

  const handleChange = ({
    selectedItems: items,
  }: {
    selectedItems: DropdownItem[];
  }) => {
    const values = items.map((item) => item.value);
    column.setFilterValue(values.length > 0 ? values : undefined);
  };

  // Increment key when filter is cleared externally to reset component state
  useEffect(() => {
    if (filterValue.length === 0) {
      keyRef.current += 1;
    }
  }, [filterValue.length]);

  return (
    <MultiSelect
      key={keyRef.current}
      id={`filter-${column.id}`}
      titleText=""
      label="All"
      size="sm"
      items={options}
      itemToString={(item: DropdownItem) => item?.label ?? ''}
      initialSelectedItems={selectedItems}
      onChange={handleChange}
      useTitleInItem
    />
  );
}
